import type { FC } from "react";

import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import { Flex } from "@similarweb/ui-components/foundations";

import { tableActionsCreator } from "app/actions/tableActions";
import { showSuccessToast } from "app/actions/toast_actions";
import type { EntityKey } from "app/common/keyword-research/serp-players/consts";
import {
    KEYWORD_ANALYSIS_PREFIX,
    TABLE_COLUMN_CPC,
    TABLE_COLUMN_MONTHLY_VOLUME,
    TABLE_COLUMN_SHARE,
    TABLE_COLUMN_WINDOW_VOLUME,
    columnNamesAndTooltipsMap,
} from "app/common/keyword-research/serp-players/consts";
import { KeywordAnalysisTrafficSource, TrafficShare, UrlCellWebsiteAnalysis } from "app/components/React/Table/cells";
import { SWReactTableWrapperWithSelection } from "app/components/React/Table/SWReactTableWrapperWithSelection";
import { KeywordSelection } from "app/components/React/TableSelectionComponents/KeywordSelection";
import { getToastItemComponent } from "app/components/React/Toast/ToastItem";
import { translate } from "app/filters/i18nFilter";
import { getTableSelectedItems } from "app/reducers/_reducers/tableSelection.reducer";
import { allTrackers } from "app/services/track/track";
import type { EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";

import {
    CloseIconButton,
    EnrichHeaderTitle,
    EnrichPageContainer,
    EnrichTrafficShareContainer,
} from "./styledComponents";
import getTableColumns from "./TableColumn";
import type { IEnrichData } from "../../pages/serp-players/types";

const ROWS_PER_PAGE = 10;
const EnrichIndexContainer = styled.div`
    margin: 0 8px 0 0;
`;
interface IOrganicEnrichComponentProps {
    getSitesForApi: (domain: string, subDomains: { Domain: string }[] | unknown) => string;
    propsInitialFilters: Record<string, string | number | boolean>;
    siteName: string;
    isKeywordsGroup: boolean;
    params: Record<string, string | number | boolean>;
    pageNumber: number;
    pageSize: number;
    row: any;
    enrichTableEndpoint: string;
    entity: EntityKey;
    visibleColumns?: string[];
    transformDataEnrich?: (data: any) => any;
    removeLinkFromEnrichTable?: boolean;
    selectedPageTab?: keyof typeof EKeywordTabs;
}

export const OrganicTableEnriched: FC<IOrganicEnrichComponentProps> = ({
    row,
    propsInitialFilters,
    getSitesForApi,
    isKeywordsGroup,
    params,
    pageNumber,
    pageSize,
    visibleColumns,
    enrichTableEndpoint,
    transformDataEnrich,
    removeLinkFromEnrichTable = false,
    selectedPageTab,
    entity,
}) => {
    const dispatch = useDispatch();
    const { Share, index, Keywords } = row;
    const initialFilters = {
        ...propsInitialFilters,
        sitesFilter: getSitesForApi(row.Domain, row.Children),
        pageSize: 10,
    };
    // on the domain call don't show the children
    const rowDataForDomainCall = {
        ...row,
        Children: [],
    };
    const selectedRows = useSelector(getTableSelectedItems("KeywordAnalysisOrganicTrafficV2_Table"));
    const clickOutsideXButton = () => {
        allTrackers.dispatchTrackEvent("Open", "Click", "Organic Clicks Over Time/Collapsed");
        document.body.click();
    };

    const tableColumnsParams = {
        isGroup: isKeywordsGroup,
        isEnrichTable: true,
        tooltips: columnNamesAndTooltipsMap,
        visibleColumns,
        duration: params.duration as string,
        country: params.country as string,
        entity,
        selectedPageTab,
        sortableFields: [TABLE_COLUMN_SHARE, TABLE_COLUMN_CPC, TABLE_COLUMN_WINDOW_VOLUME, TABLE_COLUMN_MONTHLY_VOLUME],
    };

    const transformData = (data: IEnrichData) => {
        return {
            ...data,
            records: data.Data.map((record) => {
                return {
                    ...record,
                };
            }),
            TotalCount: data.TotalCount,
        };
    };
    const clearAllSelectedRows = () => {
        dispatch(tableActionsCreator("KeywordAnalysisOrganicTrafficV2_Table", "Keyword").clearAllSelectedRows());
    };
    const showToast = (href: string, text: string, label: string) => {
        dispatch(
            showSuccessToast(
                getToastItemComponent({
                    text,
                    linkText: label,
                    href,
                    onClick: () =>
                        allTrackers.dispatchTrackEvent(
                            "Internal Link",
                            "switch",
                            "internal link/keywordAnalysis.organicTraffic",
                        ),
                }),
            ),
        );
    };
    return (
        <>
            <Flex
                justifyContent="space-between"
                padding="16px 24px"
                height="64px"
            >
                <Flex
                    justifyContent="flex-start"
                    alignItems="center"
                    flexGrow={1}
                >
                    <EnrichIndexContainer>{pageNumber * pageSize + index + 1}</EnrichIndexContainer>
                    <EnrichPageContainer className={"swReactTableCell urlCell"}>
                        {removeLinkFromEnrichTable ? (
                            <UrlCellWebsiteAnalysis
                                {...row}
                                value={row.Domain}
                                row={row}
                            />
                        ) : (
                            <KeywordAnalysisTrafficSource
                                {...rowDataForDomainCall}
                                value={row.Domain}
                                row={rowDataForDomainCall}
                                removeLink={removeLinkFromEnrichTable}
                            />
                        )}
                    </EnrichPageContainer>
                    <EnrichTrafficShareContainer>
                        <EnrichHeaderTitle>
                            {translate(`${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_SHARE}.title`)}
                        </EnrichHeaderTitle>
                        <TrafficShare
                            {...row}
                            value={Share}
                            row={row}
                        />
                    </EnrichTrafficShareContainer>
                </Flex>

                <CloseIconButton
                    type="flat"
                    onClick={clickOutsideXButton}
                    iconName="clear"
                    placement="left"
                    iconSize="sm"
                />
            </Flex>
            <SWReactTableWrapperWithSelection
                tableSelectionKey="KeywordAnalysisOrganicTrafficV2_Table"
                tableSelectionProperty="Keyword"
                serverApi={enrichTableEndpoint}
                tableColumns={getTableColumns(tableColumnsParams)}
                initialFilters={initialFilters}
                requestBody={{ Keywords }}
                rowsPerPage={ROWS_PER_PAGE}
                transformData={transformDataEnrich ?? transformData}
                recordsField="records"
                totalRecordsField="TotalCount"
                umRowsForLoader={6}
                tableHeight={164}
                pageIndent={1} // since the BE page size is 12 (rows per page) and the FE page size is 4 (rows per page) and we need the first page to start from 1 and not 0
                tableOptions={{
                    aboveHeaderComponents: [
                        <KeywordSelection
                            key="tableSelection"
                            appendTo="body"
                            clearAllSelectedRows={clearAllSelectedRows}
                            notify={true}
                            selectedRows={selectedRows}
                            showToast={showToast}
                            searchField={"Keyword"}
                        />,
                    ],
                    metric: "KeywordAnalysisOrganicTrafficV2_Table",
                }}
            />
        </>
    );
};
